import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Modal,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconSearch from "../../../assets/svg/search-icon-grey.svg";
import { ReactComponent as NoVideoIcon } from "../../../assets/svg/no-video.svg";
import axiosInstant from "../../../api";
import { getShortlistEligibleProjects } from "../../../api/endpoints";
import { DotLottleLoader } from "../../../components/Loader";
import { LoadMoreBtn } from "../../../components/Loader";
import toast from "react-hot-toast";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  shortlistId: number;
  onSuccess: () => void;
}

export default function AddProjectToShortlist({
  open,
  setOpen,
  shortlistId,
  onSuccess
}: Props) {
  const [projects, setProjects] = useState<any[]>([]);
  const [existingProjects, setExistingProjects] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [shortlistExpertIds, setShortlistExpertIds] = useState<number[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const fetchShortlistData = async () => {
    try {
      const shortlistRes = await axiosInstant.get(`/shortlists/${shortlistId}`);
      const userIds = shortlistRes.data.experts
        ?.map((expert: any) => expert?.ShortlistUser?.user_id)
        .filter((id: number | undefined) => !!id) || [];
      setShortlistExpertIds(userIds);
      return userIds;
    } catch (error) {
      console.error("Error fetching shortlist data:", error);
      return [];
    }
  };

  const fetchProjects = async (expertIds: number[], pageNum: number = 1, search: string = "") => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '20');
      params.append('page', pageNum.toString());
      if (search) params.append('search', search);
      
      const response = await axiosInstant.get(`${getShortlistEligibleProjects()}?${params.toString()}`);
      
      setTotalCount(response.data.count);
      
      const projectsWithExperts: any[] = [];
      const newProjects: any[] = response.data.rows;
      
      setExistingProjects(projectsWithExperts);
      
      if (pageNum === 1) {
        setProjects(newProjects);
      } else {
        setProjects(prev => [...prev, ...newProjects]);
      }
      
      const totalPages = Math.ceil(response.data.count / 20);
      setHasMore(pageNum < totalPages);
      
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setPage(1);
    setHasMore(true);
    setProjects([]);
    setExistingProjects([]);
    
    const timeoutId = setTimeout(() => {
      fetchShortlistData().then(async (expertIds) => {
        await fetchProjects(expertIds, 1, newSearchTerm);
      });
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const handleLoadMore = async () => {
    if (!isLoading && hasMore) {
      setIsLoading(true);
      
      try {
        const nextPage = page + 1;
        setPage(nextPage);
        await fetchProjects(shortlistExpertIds, nextPage, searchTerm);
      } catch (error) {
        console.error("Error loading more projects:", error);
        toast.error("Failed to load more projects");
      } finally {
        setIsLoading(false);
      }
    }
  };


  const handleProjectSelect = async (projectId: number) => {
    const project = projects.find((p) => p.id === projectId);
  
    if (!project || !project.brands?.[0]?.id) {
      toast.error("Invalid project data");
      return;
    }
  
    const brandId = project.brands[0].id;
  
    try {
      setIsLoading(true);
  
      const shortlistRes = await axiosInstant.get(`/shortlists/${shortlistId}`);
  
      const userIds = shortlistRes.data.experts
        ?.map((expert: any) => expert?.ShortlistUser?.user_id)
        .filter((id: number | undefined) => !!id);
  
      if (!userIds || userIds.length === 0) {
        toast.error("No experts with valid user IDs found.");
        return;
      }

      const response = await axiosInstant.post(`/brands/${brandId}/projects/${projectId}/pros`, {
        user_ids: userIds
      });
      if (response.data) {
      toast.success("Experts added to project successfully");
      onSuccess();
      setOpen(false);
      }
    } catch (error: any) {
      console.error("Error adding experts to project:", error);
      const errorMessage = error.response?.data?.error || "Failed to add experts to project";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderProject = (project: any) => (
    <div
      key={project.id}
      onClick={() => handleProjectSelect(project.id)}
      className="p-4 border border-border-token rounded-xl cursor-pointer transition-all hover:bg-surface-2"
    >
      <div className="flex justify-between items-center">
        <div className="flex gap-4">
          <div className="w-[64px] h-[64px] rounded-xl bg-tint flex items-center justify-center overflow-hidden">
            {project.product?.image ? (
              <img
                src={project.product.image}
                alt={project.product?.title || 'Product'}
                className="w-full h-full object-cover"
              />
            ) : project.brands?.[0]?.logo ? (
              <img
                src={project.brands[0].logo}
                alt={project.brands[0].name}
                className="w-full h-full object-contain p-2"
              />
            ) : (
              <div className="w-full h-full bg-tint" />
            )}
          </div>
          <div>
            <h3 className="text-[16px] font-bold text-text-1">
              {project.title}
            </h3>
            <p className="text-[14px] text-text-2">
              {project.brands?.[0]?.name || 'No brand'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      fetchShortlistData().then(async (expertIds) => {
        setShortlistExpertIds(expertIds);
        await fetchProjects(expertIds, 1, searchTerm);
        setIsLoading(false);
      });
    } else {
      setProjects([]);
      setExistingProjects([]);
      setPage(1);
      setHasMore(true);
      setSearchTerm("");
      setShortlistExpertIds([]);
      setTotalCount(0);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box
        component={Paper}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: 'calc(100% - 24px)', md: '90vw', lg: 800 },
          maxWidth: 800,
          maxHeight: { xs: 'calc(100vh - 24px)', sm: '90vh' },
          bgcolor: "var(--surface)",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          padding: { xs: '12px', sm: '24px' },
          overflowY: "auto",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ padding: { xs: '8px 4px', sm: '16px' }, gap: 1.5 }}
        >
          <div className="flex items-center gap-4 sm:gap-10 flex-1 min-w-0">
            <Typography
              sx={{
                color: "var(--Base-1000, #000)",
                fontFamily: '"Circular Std", sans-serif',
                fontSize: { xs: 18, sm: 24 },
                fontWeight: 900,
                lineHeight: "132%",
                whiteSpace: "nowrap",
              }}
            >
              Add Project
            </Typography>

            <div className="flex-1 relative min-w-0">
              <img
                className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
                src={IconSearch}
                alt="search"
              />
              <input
                type="text"
                className="w-full sm:max-w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
                placeholder="Search projects"
                onChange={handleSearchChange}
                value={searchTerm}
              />
            </div>
          </div>
          <IconButton onClick={() => setOpen(false)} className="shrink-0">
            <CloseIcon />
          </IconButton>
        </Box>

        <hr style={{ margin: "0px 8px", color: "var(--border)" }} />

        <div className="mt-4 space-y-4 px-2 sm:px-4">
          {isLoading && page === 1 ? (
            <div className="flex justify-center items-center p-8">
              <DotLottleLoader />
            </div>
          ) : (
            <>
              {projects.map((project) => renderProject(project))}

              {projects.length === 0 && existingProjects.length === 0 && !isLoading && (
                <div className="flex flex-col items-center justify-center gap-6 py-16">
                  <NoVideoIcon />
                  <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal not-italic text-center">
                    No projects found.<br />
                    Only projects that haven't passed Expert Review are available for adding shortlists.
                  </div>
                </div>
              )}

              {hasMore && (
                <LoadMoreBtn onClick={handleLoadMore} loading={isLoading} customClasses="!my-6" />
              )}
              
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
} 